/**
 * Global React Query retry policy.
 *
 * The QueryClient runs with `networkMode: 'always'` (see index.js — a deliberate
 * fix for browsers that misreport `navigator.onLine`), so queries fire even when
 * the network is down and nothing pauses a failing query. With React Query's
 * default `retry: 3` on every error, one offline user produced hundreds of RUM
 * errors an hour, and 4xx responses that can never succeed were retried too.
 *
 * What this policy changes:
 *  - a non-transient 4xx (400/401/403/404/...) is never retried;
 *  - deterministic client-side failures (a JSON parse error on a 200, an
 *    aborted request) are never retried;
 *  - everything else (network failure, 5xx, 408/425/429, the batch-routing
 *    "server save still in flight" 409) gets up to MAX_QUERY_RETRIES retries
 *    on React Query's default backoff (1s, 2s, ...).
 *
 * MAX_QUERY_RETRIES is 2, not 1: the shared state hooks disable refetch on
 * mount/focus/reconnect, so a screen whose query has errored stays errored
 * until the user navigates. Two retries keep the ~3 s recovery window that a
 * brief connection blip needs; one retry (1 s) strands "It belongs to another
 * teacher's class" style screens on a hiccup.
 *
 * Errors thrown by service.js carry `status` and `code` (see `apiError`).
 */

// 4xx responses that are transient by definition and worth another attempt.
// (service.js errors carry no headers, so Retry-After cannot be honoured here.)
const RETRYABLE_CLIENT_STATUSES = new Set([408, 425, 429]);

// 409 codes the backend uses to mean "try again shortly", not "this conflicts":
// BATCH_ROUTING_BUSY = the original bulk-upload save is still being applied and
// the idempotent replay will succeed once it finishes (see utils/pendingBatch.js).
const RETRYABLE_CONFLICT_CODES = new Set(['BATCH_ROUTING_BUSY']);

export const MAX_QUERY_RETRIES = 2;

const isDeterministicClientFailure = (error) =>
  error instanceof SyntaxError || error?.name === 'AbortError';

export function isRetryableQueryError(error) {
  if (isDeterministicClientFailure(error)) return false;
  const status = Number(error?.status);
  if (!Number.isFinite(status) || status <= 0) {
    // No HTTP status: network-level failure (offline, DNS, CORS).
    return true;
  }
  if (status >= 500) return true;
  if (status === 409) return RETRYABLE_CONFLICT_CODES.has(error?.code);
  if (status >= 400) return RETRYABLE_CLIENT_STATUSES.has(status);
  return true;
}

/**
 * `retry` callback for QueryClient defaultOptions.queries.
 * @param {number} failureCount attempts that have already failed
 * @param {unknown} error the error thrown by the last attempt
 */
export function shouldRetryQuery(failureCount, error) {
  if (failureCount >= MAX_QUERY_RETRIES) return false;
  return isRetryableQueryError(error);
}
